import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Store, TrendingUp, Clock, CreditCard, Bell, BarChart3, 
  ArrowRight, CheckCircle, Users, Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function VendorLanding() {
  const { user, openSignIn } = useAuth();

  const handleGetStarted = () => {
    if (!user) {
      openSignIn();
    }
  };

  const features = [
    {
      icon: Clock,
      title: 'Scheduled Pickups',
      description: 'Students pre-order and pick up at set times. No more rush hour chaos.'
    },
    {
      icon: CreditCard,
      title: 'Guaranteed Payment',
      description: 'All orders are prepaid via Stripe. No cash handling, no no-shows.'
    },
    {
      icon: Bell,
      title: 'Real-Time Orders',
      description: 'Get instant notifications when new orders come in. Never miss a sale.'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Track your daily revenue, popular items, and peak hours.'
    },
    {
      icon: Users,
      title: 'Campus Reach',
      description: 'Access thousands of hungry UofT students between classes.'
    },
    {
      icon: Smartphone,
      title: 'Easy Management',
      description: 'Update your menu, hours, and availability from any device.'
    }
  ];

  const steps = [
    { num: 1, title: 'Sign Up', desc: 'Create your vendor account with Google or Apple' },
    { num: 2, title: 'Register Your Truck', desc: 'Add your truck info, hours, and menu' },
    { num: 3, title: 'Get Approved', desc: 'Our team reviews your application' },
    { num: 4, title: 'Start Selling', desc: 'Go live and receive orders!' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-black/5">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src={`${process.env.PUBLIC_URL}/icon.png`}
              alt="SkipdQ"
              className="w-9 h-9 rounded-xl object-cover"
            />
            <span className="font-bold text-lg tracking-tight">
              Skipd<span className="text-primary">Q</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" size="sm">For Students</Button>
            </Link>
            {user ? (
              <Link to={user.role === 'vendor' ? '/dashboard' : '/vendor/register'}>
                <Button size="sm" className="rounded-full">
                  {user.role === 'vendor' ? 'Dashboard' : 'Continue Registration'}
                </Button>
              </Link>
            ) : (
              <Button size="sm" className="rounded-full" onClick={openSignIn} data-testid="vendor-signin-btn">
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <Store className="w-4 h-4" />
              <span className="text-sm font-medium">For Food Truck Owners</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Grow Your Business<br />
              <span className="text-primary">on Campus</span>
            </h1>
            
            <p className="text-base sm:text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join SkipdQ and reach thousands of hungry UofT students. 
              Pre-orders mean no wasted food, guaranteed payments, and happier customers.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <Link to={user.role === 'vendor' ? '/dashboard' : '/vendor/register'}>
                  <Button size="lg" className="rounded-full text-base px-8" data-testid="get-started-btn">
                    {user.role === 'vendor' ? 'Go to Dashboard' : 'Complete Registration'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <Button 
                  size="lg" 
                  className="rounded-full text-base px-8" 
                  onClick={handleGetStarted}
                  data-testid="get-started-btn"
                >
                  Get Started Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              <Link to="/">
                <Button variant="outline" size="lg" className="rounded-full text-base px-8">
                  See How It Works
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-muted/50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-primary">50K+</p>
              <p className="text-sm text-muted-foreground">Students on Campus</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-primary">0%</p>
              <p className="text-sm text-muted-foreground">Platform Fee (Launch)</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-primary">24hr</p>
              <p className="text-sm text-muted-foreground">Approval Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Food Trucks Love SkipdQ</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Everything you need to run a successful campus food business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-card rounded-xl p-6 border shadow-sm"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-muted/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Get Started in 4 Easy Steps</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 + 0.3 }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {step.num}
                </div>
                <h3 className="font-semibold mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-primary rounded-3xl p-12 text-primary-foreground"
          >
            <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Business?</h2>
            <p className="text-primary-foreground/80 mb-8 max-w-lg mx-auto">
              Join SkipdQ today and start receiving pre-orders from hungry students. 
              No upfront costs, no long-term commitments.
            </p>
            {user ? (
              <Link to={user.role === 'vendor' ? '/dashboard' : '/vendor/register'}>
                <Button size="lg" variant="secondary" className="rounded-full text-base px-8">
                  {user.role === 'vendor' ? 'Go to Dashboard' : 'Complete Registration'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            ) : (
              <Button 
                size="lg" 
                variant="secondary" 
                className="rounded-full text-base px-8" 
                onClick={handleGetStarted}
              >
                Sign Up Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img
              src={`${process.env.PUBLIC_URL}/icon.png`}
              alt=""
              className="w-6 h-6 rounded object-cover"
            />
            <span className="text-sm text-muted-foreground">SkipdQ © 2026</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground flex-wrap justify-center">
            <Link to="/" className="hover:text-foreground">Student App</Link>
            <Link to="/privacy-policy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <a href="mailto:vendors@skipdq.ca" className="hover:text-foreground">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
